import { useState, useEffect } from 'react'
import { FileText, Download, Calendar, Euro, Eye  } from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import toast from 'react-hot-toast'
import api from '../../../services/api'

export default function PatientInvoicesTab({ patientId }) {
    const [invoices, setInvoices] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        fetchInvoices()
    }, [patientId])

    const fetchInvoices = async () => {
        try {
            const res = await api.get(`/api/invoices/patient/${patientId}`)
            setInvoices(res.data)
        } catch (error) {
            console.error(error)
            toast.error("Impossible de charger les factures")
        } finally {
            setLoading(false)
        }
    }

    const handlePreview = async (invoice) => {
        try {
            toast.loading("Ouverture...", { id: 'preview_inv' })
            const newWindow = window.open('', '_blank');
            const response = await api.get(`/api/invoices/${invoice.id}/download`, { responseType: 'blob' })
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            newWindow.location.href = url;
            toast.success("Aperçu généré !", { id: 'preview_inv' })
        } catch (error) {
            toast.error("Erreur aperçu", { id: 'preview_inv' })
        }
    }

    const handleDownload = async (invoice) => {
        try {
            toast.loading("Téléchargement...")
            
            const response = await api.get(`/api/invoices/${invoice.id}/download`, {
                responseType: 'blob'
            })
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `${invoice.invoice_number}_Facture.pdf`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.dismiss()
            toast.success("Téléchargé !")
        } catch (error) {
            toast.dismiss()
            toast.error("Erreur téléchargement")
        }
    }

    const getPrice = (type) => type === 'new' ? '60.00 €' : '40.00 €'
    const getTypeLabel = (type) => type === 'new' ? '1ère Consultation' : 'Suivi'

    if (loading) return <div className="p-12 text-center"><div className="spinner"/></div>

    return (
        <div className="space-y-6">
            <h3 className="text-lg font-serif text-sage-700 flex items-center gap-2">
                <FileText className="w-5 h-5" /> Historique de facturation
            </h3>

            {invoices.length === 0 ? (
                <div className="text-center p-8 border-2 border-dashed border-gray-100 rounded-xl bg-gray-50 text-gray-400">
                    <p>Aucune facture générée pour ce patient.</p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-white">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4">Date RDV</th>
                                <th className="px-6 py-4">N° Facture</th>
                                <th className="px-6 py-4">Prestation</th>
                                <th className="px-6 py-4 text-right">Montant</th>
                                <th className="px-6 py-4 text-right">Mode</th>
                                <th className="px-6 py-4 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {invoices.map((inv) => (
                                <tr key={inv.id} className="hover:bg-sage-50 transition-colors">
                                    <td className="px-6 py-4 font-medium text-gray-700">
                                        {format(new Date(inv.date), 'dd MMMM yyyy', { locale: fr })}
                                    </td>
                                    <td className="px-6 py-4 font-mono text-gray-500 text-xs">
                                        {inv.invoice_number}
                                    </td>
                                    <td className="px-6 py-4 text-gray-600">
                                        {getTypeLabel(inv.patient_type)}
                                    </td>
                                    <td className="px-6 py-4 text-right font-bold text-gray-800">
                                        {getPrice(inv.patient_type)}
                                    </td>
                                    <td className="px-6 py-4 text-right text-xs uppercase text-gray-400">
                                        {inv.payment_method || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <button 
                                                onClick={() => handlePreview(inv)}
                                                className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-white border border-transparent hover:border-blue-200 transition-all"
                                                title="Aperçu du PDF"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => handleDownload(inv)}
                                                className="text-sage-600 hover:text-sage-800 p-2 rounded-full hover:bg-white border border-transparent hover:border-sage-200 transition-all"
                                                title="Télécharger le PDF"
                                            >
                                                <Download className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}